import { ActionIcon, Menu, Stack, Text, UnstyledButton } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import {
  EllipsisVertical,
  Folder as FolderIcon,
  FolderInput,
  FolderOpen,
  FolderPlus,
  Pencil,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState, type DragEvent } from "react";

import {
  getListFoldersQueryKey,
  useCreateFolder,
  useListFolders,
  useRenameFolder,
} from "../../api/generated/endpoints/teams/teams";
import type { Folder } from "../../api/generated/model";
import { refreshLibraryLists } from "../../api/library-lists";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { MoveBatchError, movedWords, useMoveFiles } from "../../platform/media/MoveToProject";
import { folderTree } from "../../platform/folders/folder-tree";
import { FolderNameField } from "../../platform/folders/FolderTreeList";
import { useMembership } from "../../platform/membership";
import { canEditContent } from "../../platform/permissions";
import { REINDEX_NOTE_MS } from "./bulk-actions";
import { allIn, isCardDrag, type CardDrag } from "./card-drag";
import { DeleteFolderDialog, MoveFolderDialog } from "./FolderDialogs";
import classes from "./library.module.css";

interface FoldersSectionProps {
  teamId: string;
  brandId: string;
  /** The library's folder; `undefined` is "All assets", whose cards are the top level. */
  folderId: string | undefined;
  onChoose: (folderId: string | undefined) => void;
  /** A card's drag under way (spec library-card-drag): a tile takes it as it passes. */
  dragging: { readonly current: CardDrag | null };
  /** After dropped files moved: the selection clears, as after the bulk bar's Move. */
  onMoved: () => void;
}

/** Live's toast after a move (`useMoveAssetToProjectNotifications`), as a line under "Projects". */
export function movedToProject(count: number): string {
  return count === 1 ? "Asset moved to project" : `${count} assets moved to project`;
}

/**
 * Live's folders view (`ProjectsSection`, #446, design-ref/folders-410/10-15): "Projects" with
 * a folder-plus button, then a card per subproject of the library's folder (the top level on
 * "All assets"), each with its own subprojects' count and live's ⋮ menu (Rename, Move, Delete).
 * A card opens its folder; the button makes a subproject there.
 */
export function FoldersSection(props: FoldersSectionProps) {
  const { teamId, brandId, folderId, onChoose, dragging, onMoved } = props;
  const queryClient = useQueryClient();
  const me = useMembership(teamId);
  const folders = useListFolders(teamId, brandId);
  const tree = useMemo(() => folderTree(folders.data ?? []), [folders.data]);
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<string | undefined>(undefined);
  const [moving, setMoving] = useState<Folder | undefined>(undefined);
  const [deleting, setDeleting] = useState<Folder | undefined>(undefined);
  const ids = { teamId, brandId };
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: getListFoldersQueryKey(teamId, brandId) });
  const create = useCreateFolder({
    mutation: {
      onSuccess: async () => {
        setCreating(false);
        await refresh();
      },
    },
  });
  const rename = useRenameFolder({
    mutation: {
      onSuccess: async () => {
        setRenaming(undefined);
        await refresh();
      },
    },
  });
  // Live's drop of cards on a tile (spec library-card-drag): the tile under the drag, and the
  // files a drop moved, said for live's toast's 5 s as Re-index's line.
  const moveFiles = useMoveFiles(teamId, brandId);
  const [over, setOver] = useState<string | null>(null);
  const [moved, setMoved] = useState<number | null>(null);
  useEffect(() => {
    if (moved === null) return;
    const timer = window.setTimeout(() => setMoved(null), REINDEX_NOTE_MS);
    return () => window.clearTimeout(timer);
  }, [moved]);
  // A tile refuses a drag while a move is under way, so no drop replaces another's result (the
  // AO review of #1209), or where every dragged file is in it already (live's refusal).
  const refused = (folder: Folder) =>
    moveFiles.isPending || (dragging.current !== null && allIn(dragging.current, folder.id));
  const dropOn = (folder: Folder) => ({
    onDragEnter: (event: DragEvent<HTMLDivElement>) => {
      if (!isCardDrag(event)) return;
      event.preventDefault();
      setOver(folder.id);
    },
    onDragOver: (event: DragEvent<HTMLDivElement>) => {
      if (!isCardDrag(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = refused(folder) ? "none" : "move";
    },
    onDragLeave: (event: DragEvent<HTMLDivElement>) => {
      const next = event.relatedTarget;
      if (next instanceof Node && event.currentTarget.contains(next)) return;
      setOver((current) => (current === folder.id ? null : current));
    },
    onDrop: (event: DragEvent<HTMLDivElement>) => {
      if (!isCardDrag(event)) return;
      event.preventDefault();
      setOver(null);
      const drag = dragging.current;
      if (drag === null || refused(folder)) return;
      setMoved(null);
      moveFiles.mutate(
        { assetIds: drag.assetIds, folderId: folder.id },
        {
          onSuccess: () => {
            setMoved(drag.assetIds.length);
            onMoved();
          },
        },
      );
    },
  });
  const moveFailed = moveFiles.error instanceof MoveBatchError ? moveFiles.error : null;
  const movedPart = moveFailed === null ? null : movedWords(moveFailed);
  const mayChange = (folder: Folder) =>
    me !== undefined && canEditContent(me.role, folder.created_by, me.userId);
  const cards = tree.children(folderId ?? null);
  const failed = create.error ?? rename.error;

  const card = (folder: Folder) => {
    const count = tree.children(folder.id).length;
    return (
      <div
        key={folder.id}
        className={classes.folderCard}
        data-drop={over === folder.id ? (refused(folder) ? "refused" : "over") : undefined}
        {...dropOn(folder)}
      >
        {renaming === folder.id ? (
          <FolderNameField
            initial={folder.name}
            placeholder="Project name"
            label={`Rename ${folder.name}`}
            pending={rename.isPending}
            onSubmit={(name) => rename.mutate({ ...ids, folderId: folder.id, data: { name } })}
            onCancel={() => {
              rename.reset();
              setRenaming(undefined);
            }}
          />
        ) : (
          <UnstyledButton className={classes.folderCardOpen} onClick={() => onChoose(folder.id)}>
            <FolderIcon size={18} strokeWidth={1.5} aria-hidden />
            <span className={classes.folderName} title={folder.name}>
              {folder.name}
            </span>
          </UnstyledButton>
        )}
        {count > 0 ? (
          <span className={classes.folderCount} aria-label={`${count} ${count === 1 ? "subproject" : "subprojects"}`}>
            {count}
          </span>
        ) : null}
        {mayChange(folder) ? (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" size="sm" aria-label={`Actions for ${folder.name}`}>
                <EllipsisVertical size={16} aria-hidden />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<Pencil size={14} aria-hidden />}
                onClick={() => setRenaming(folder.id)}
              >
                Rename
              </Menu.Item>
              <Menu.Item
                leftSection={<FolderInput size={14} aria-hidden />}
                onClick={() => setMoving(folder)}
              >
                Move
              </Menu.Item>
              <Menu.Item
                color="red"
                leftSection={<Trash2 size={14} aria-hidden />}
                onClick={() => setDeleting(folder)}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : null}
      </div>
    );
  };

  return (
    <section aria-label="Projects" className={classes.foldersSection}>
      <div className={classes.sectionHead}>
        <Text fw={600} size="md">
          Projects
        </Text>
        <ActionIcon variant="subtle" aria-label="Create project" onClick={() => setCreating(true)}>
          <FolderPlus size={16} aria-hidden />
        </ActionIcon>
      </div>
      {folders.isError ? <ErrorAlert error={folders.error} /> : null}
      {failed === null ? null : <ErrorAlert error={failed} />}
      {moved === null ? null : (
        <Text size="sm" component="output">
          {movedToProject(moved)}
        </Text>
      )}
      {moveFailed === null ? null : (
        <Stack gap={4}>
          {movedPart === null ? null : (
            <Text size="sm" component="output">
              {movedPart}
            </Text>
          )}
          <ErrorAlert error={moveFailed.cause} />
        </Stack>
      )}
      {folders.isSuccess && cards.length === 0 && !creating ? (
        <div className={classes.foldersEmpty}>
          <FolderOpen size={21} aria-hidden />
          <Text size="sm">
            {folderId === undefined
              ? "You don't have projects yet"
              : "This project has no subprojects yet"}
          </Text>
        </div>
      ) : (
        <div className={classes.folderGrid}>
          {creating ? (
            <div className={classes.folderCard} data-new>
              <FolderNameField
                initial=""
                placeholder="New project"
                label="New project"
                pending={create.isPending}
                onSubmit={(name) =>
                  create.mutate({
                    ...ids,
                    data: { name, ...(folderId === undefined ? {} : { parent_id: folderId }) },
                  })
                }
                onCancel={() => {
                  create.reset();
                  setCreating(false);
                }}
              />
            </div>
          ) : null}
          {cards.map(card)}
        </div>
      )}
      <MoveFolderDialog
        {...ids}
        tree={tree}
        folder={moving}
        onClose={() => setMoving(undefined)}
        onMoved={async () => {
          await refresh();
        }}
      />
      <DeleteFolderDialog
        {...ids}
        tree={tree}
        folder={deleting}
        onClose={() => setDeleting(undefined)}
        onDeleted={async () => {
          await Promise.all([refresh(), refreshLibraryLists(queryClient, teamId, brandId)]);
        }}
      />
    </section>
  );
}
